import { useEffect, useRef, useState } from 'react';
import type AskAllTrailsQueryType from '@alltrails/amplitude/enums/AskAllTrailsQueryType';
import Button from '@alltrails/denali/components/Button';
import Heading from '@alltrails/denali/components/Heading';
import IconButton from '@alltrails/denali/components/IconButton';
import PlusLogoBadge from '@alltrails/denali/components/PlusLogoBadge/PlusLogoBadge';
import StatusBadge from '@alltrails/denali/components/StatusBadge';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import Add from '@alltrails/denali/icons/Add';
import Close from '@alltrails/denali/icons/Close';
import PageUIMapHoverProvider from '@alltrails/maps/components/PageUIMapHoverProvider/PageUIMapHoverProvider';
import { useSidebar } from '@alltrails/maps/components/Sidebar/useSidebar';
import useActiveAskTurn from '@/hooks/useActiveAskTurn';
import useAskAccess from '@/hooks/useAskAccess';
import useAskQuestion from '@/hooks/useAskQuestion';
import useAskResponse from '@/hooks/useAskResponse';
import useIsAskChatEnabled from '@/hooks/useIsAskChatEnabled';
import { useAppDispatch, useAppSelector } from '@/utils/redux';
import { clearAskQuestion, exitAsk } from '@/utils/slices/askSlice';
import AskActionProvider from './AskActionProvider';
import AskAiNotice from './AskAiNotice';
import AskEmptyState from './AskEmptyState';
import AskInput from './AskInput';
import AskResponse from './AskResponse';
import AskSaveProvider from './AskSaveProvider';
import styles from './AskSidebar.module.scss';
import AskTurn from './AskTurn';

const AskSidebar = () => {
  const dispatch = useAppDispatch();
  const isMobile = useIsMobileSizedScreen();
  const { request, ...response } = useAskResponse();
  const turns = useAppSelector(state => state.ask.turns);
  const isChat = useIsAskChatEnabled();
  const ask = useAskQuestion();
  const latestTurnRef = useRef<HTMLDivElement>(null);
  const chatTranscriptRef = useRef<HTMLDivElement>(null);
  // TODO(XPLR-256): real analytics values
  const { modals, withAskAccess } = useAskAccess({ signUpTrigger: 'Banner on Explore' });
  const [question, setQuestion] = useState(isChat ? '' : (request?.query ?? ''));

  // Brings each new question into view at the bottom of the transcript, like the next message in a chat
  useEffect(() => {
    if (isChat) {
      latestTurnRef.current?.scrollIntoView({ block: 'end' });
    }
  }, [isChat, turns.length]);

  useActiveAskTurn(chatTranscriptRef, turns);

  const title = 'Ask AllTrails'; // hwandrew TODO: XPLR-204 translations
  const closeLabel = 'Close Ask AllTrails'; // hwandrew TODO: XPLR-204 translations
  const betaLabel = 'Beta'; // hwandrew TODO: XPLR-204 translations
  const newChatLabel = 'New'; // hwandrew TODO: XPLR-204 translations
  const newChatTitle = 'Start a new chat'; // hwandrew TODO: XPLR-204 translations

  const plusBadge = <PlusLogoBadge size="sm" variant="icon" />;
  const betaBadge = <StatusBadge emphasis="mid" size="sm" text={betaLabel} variant="neutral" />;

  const closeButton = <IconButton icon={{ Component: Close }} onClick={() => dispatch(exitAsk())} size="sm" title={closeLabel} />;

  const headerButtons =
    isChat && turns.length > 0 ? (
      <div className={styles.headerButtons}>
        <Button
          icon={{ Component: Add }}
          onClick={() => dispatch(clearAskQuestion())}
          size="sm"
          text={newChatLabel}
          title={newChatTitle}
          variant="ghost"
        />
        {closeButton}
      </div>
    ) : (
      closeButton
    );

  const { SidebarHeader, SidebarContent } = useSidebar({ additionalButton: headerButtons, collapseBehavior: 'none', fullHeight: true });

  // Outside a chat the question stays in the input after it's asked, like a search bar, so it can be edited and asked again.
  // In a chat it moves into the transcript, leaving the input for the next one.
  const submit = (text: string, queryType: AskAllTrailsQueryType) => {
    setQuestion(isChat ? '' : text);
    withAskAccess(() => ask(text, queryType));
  };

  const transcript = isChat ? (
    <div className={styles.chatTranscript} ref={chatTranscriptRef} role="log">
      {turns.map((args, index) => (
        <AskTurn args={args} key={args.requestId} ref={index === turns.length - 1 ? latestTurnRef : undefined} />
      ))}
    </div>
  ) : (
    request && <AskResponse {...response} request={request} />
  );

  const content = request ? (
    <PageUIMapHoverProvider>
      <AskActionProvider>
        <AskSaveProvider>
          <div className={styles.transcript}>{transcript}</div>
        </AskSaveProvider>
      </AskActionProvider>
    </PageUIMapHoverProvider>
  ) : (
    <AskEmptyState onSuggestionClick={suggestion => submit(suggestion, 'suggestion')} />
  );

  const body = (
    <div className={styles.body}>
      {content}
      {!isChat && <AskAiNotice />}
    </div>
  );

  const searchBar = (
    <div className={isChat ? styles.composer : styles.searchBar}>
      <AskInput onChange={setQuestion} onSubmit={text => submit(text, 'open query')} value={question} />
    </div>
  );

  // In a chat the notice sits under the input, outside the scrolling transcript, so it stays in view
  const composer = isChat && (
    <>
      {searchBar}
      <AskAiNotice />
    </>
  );

  if (isMobile) {
    return (
      <div className={styles.mobileContainer}>
        <div className={styles.mobileHeader}>
          <div className={styles.title}>
            {plusBadge}
            <Heading as="h2" size="300">
              {title}
            </Heading>
            {betaBadge}
          </div>
          {headerButtons}
        </div>
        {!isChat && searchBar}
        <div className={styles.mobileBody}>{body}</div>
        {composer}
        {modals}
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {SidebarHeader(
        <span className={styles.title}>
          {plusBadge}
          <span>{title}</span>
          {betaBadge}
        </span>
      )}
      {!isChat && searchBar}
      {SidebarContent(body, { className: styles.fillContent })}
      {composer}
      {modals}
    </div>
  );
};

export default AskSidebar;
